import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import {
  unifyCctvThresholdsToPool,
  type CctvThresholdUnifyStats,
} from '../../services/thresholdUnifyService';

type Props = {
  poolId: number;
  poolName?: string;
  /** 시설에 적용되는 특별관리구역 체류(초) */
  specialZoneDwellSec: number;
  /** 시설 검출 conf 표시 (null이면 시스템 승계 문구) */
  detectionConfLabel: string;
  /** 시설 연속 프레임 표시 */
  detectionHitsLabel: string;
  cctvCount?: number;
  compact?: boolean;
  onDone?: (stats: CctvThresholdUnifyStats) => void;
};

const PoolCctvThresholdUnifyCard: React.FC<Props> = ({
  poolId,
  poolName,
  specialZoneDwellSec,
  detectionConfLabel,
  detectionHitsLabel,
  cctvCount,
  compact = false,
  onDone,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastStats, setLastStats] = useState<CctvThresholdUnifyStats | null>(null);

  const facilitySummary = useMemo(
    () =>
      [
        `특별구역 ${specialZoneDwellSec}초`,
        `conf ${detectionConfLabel}`,
        `연속 ${detectionHitsLabel}`,
      ].join(' · '),
    [specialZoneDwellSec, detectionConfLabel, detectionHitsLabel]
  );

  const runUnify = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    try {
      const res = await unifyCctvThresholdsToPool(poolId);
      setLastStats(res.stats);
      onDone?.(res.stats);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (e as Error)?.message ||
        'CCTV 임계 통일에 실패';
      setError(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        이 시설 CCTV에만 저장된 임계(특별구역 체류·검출 conf/연속·motion 개별값)를
        지우고, <strong>시설 기준</strong>을 따르도록 맞춤.
        {cctvCount != null && cctvCount >= 0
          ? ` (등록 CCTV ${cctvCount}대)`
          : ''}
      </Typography>
      <Typography variant="caption" color="primary.main" display="block" sx={{ mb: 1.5 }}>
        시설 기준: {facilitySummary}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {lastStats ? (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {lastStats.cctvs_updated > 0
            ? `CCTV ${lastStats.cctvs_updated}/${lastStats.cctvs_scanned}대를 시설 기준으로 통일함`
            : `변경할 CCTV 임계 오버라이드 없음. (${lastStats.cctvs_scanned}대)`}
        </Alert>
      ) : null}
      <Button
        size={compact ? 'small' : 'medium'}
        variant="outlined"
        color="primary"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
        disabled={busy || !Number.isFinite(poolId) || poolId <= 0}
        onClick={() => setConfirmOpen(true)}
      >
        CCTV 임계 → 시설 기준 통일
      </Button>
    </>
  );

  return (
    <>
      {compact ? (
        <Box sx={{ mb: 2 }}>{body}</Box>
      ) : (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'secondary.light' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              CCTV 임계 → 시설 기준 통일
              {poolName ? ` · ${poolName}` : ''}
            </Typography>
            {body}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>CCTV 임계를 시설 기준으로 통일 확인</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" paragraph>
              시설 <strong>{poolName || `#${poolId}`}</strong> 소속 CCTV에서 아래 개별
              설정을 <strong>삭제</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <li>특별관리구역 체류(초)</li>
              <li>검출 신뢰도 / 연속 프레임</li>
              <li>CCTV 전용 motion 민감도·정지 (→ 시설 motion 사용)</li>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              적용 기준: {facilitySummary}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            취소
          </Button>
          <Button variant="contained" onClick={() => void runUnify()} disabled={busy}>
            통일 실행
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PoolCctvThresholdUnifyCard;
